import React, { useState, useEffect, useMemo } from 'react';
import {
     Modal,
     Box,
     Typography,
     IconButton,
     Tooltip,
     Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddTaskIcon from '@mui/icons-material/AddTask';
import { MilestoneModalProps } from './types';
import { MilestoneSelector, TaskSelector, MilestoneForm } from './components';
import { useMilestoneModal } from './hooks/useMilestoneModal';
import { styles } from './MilestoneModal.styles';
import { DIVISION_SETTINGS_MAX_NUMBER_OF_ADDITIONAL_TASKS } from '../../../../../../utils/constants';
import { useDialogBoxContext } from '../../../../../../components/decisionDialogBox/decisionDialogBoxContext';
import { decisionDialogBoxTitle, decisionDialogBoxMessage } from '../../../../../../components/decisionDialogBox/decisionDialogBox';
import { useSnackbarContext } from '../../../../../../components/globalSnackNotify/globalSnackNotifyContext';

export const MilestoneModal: React.FC<MilestoneModalProps> = ({
     open,
     onClose,
     onSave,
     availableMilestones,
     availableTasks,
     currentMilestones,
     divisionCode,
     mode,
     onReorderTasks,
}) => {
     // State to control dynamic gap between sections
     const [contentGap, setContentGap] = useState<number>(2);
     const [isAddingTaskToExisting, setIsAddingTaskToExisting] = useState<boolean>(false);
     const [canSaveNewMilestone, setCanSaveNewMilestone] = useState<boolean>(false);
     const { showDecisionDialogBox } = useDialogBoxContext();
     const { showSnackbar } = useSnackbarContext();

     const {
          selectedMilestone,
          selectedTasks,
          isEditing,
          isAddingMilestone,
          newMilestoneName,
          newTaskName,
          localMilestones,
          setSelectedTasks,
          setIsAddingMilestone,
          setNewMilestoneName,
          setNewTaskName,
          handleMilestoneSelect,
          handleDeleteTask,
          handleRenameTask,
          handleSaveNewMilestone,
          handleClose,
     } = useMilestoneModal({
          availableMilestones,
          availableTasks,
          currentMilestones,
          divisionCode,
          mode,
          onSave,
          onClose,
     });

     // Update gap based on content state
     useEffect(() => {
          if (isEditing) {
               setContentGap(1);
          } else if (isAddingMilestone) {
               setContentGap(3);
          } else if (selectedMilestone && selectedTasks.length > 0) {
               setContentGap(2.5);
          } else {
               setContentGap(2);
          }
     }, [isEditing, isAddingMilestone, selectedMilestone, selectedTasks.length]);

     const disabledAddTaskButton = useMemo(() => {
          if (!selectedMilestone) return true;

          const currentCustomTaskCount = localMilestones.filter((milestone) => (milestone.mileStoneName === selectedMilestone) && (milestone.isDefault === null)).length;
          return currentCustomTaskCount >= DIVISION_SETTINGS_MAX_NUMBER_OF_ADDITIONAL_TASKS;
     }, [localMilestones, selectedMilestone])

     const handleModalBackdropClose = (_event: object, reason: string) => {
          if (reason === 'backdropClick' && isAddingMilestone && canSaveNewMilestone) {
               showDecisionDialogBox(
                    true,
                    decisionDialogBoxTitle,
                    decisionDialogBoxMessage,
                    () => {
                         handleSaveNewMilestone();
                         handleClose();
                    },
                    () => {
                         handleClose();
                         showSnackbar("Changes discarded", "info");

                    }
               );
               return;
          }
          handleClose();
     };

     return (
          <Modal
               open={open}
               onClose={handleModalBackdropClose}
          >
               <Box sx={styles.modalContainer}>
                    <Box sx={{
                         ...styles.header,
                         gap: contentGap,
                    }}>
                         <Typography variant="h5" sx={styles.title}>
                              Customize Milestones and Tasks
                         </Typography>
                         {!isAddingMilestone && (
                              <Tooltip title="Close" arrow placement="left">
                                   <IconButton onClick={handleClose} size="small">
                                        <CloseIcon />
                                   </IconButton>
                              </Tooltip>
                         )}
                    </Box>
                    <Divider />
                    <Box sx={{
                         ...styles.content,
                         gap: contentGap,
                         overflow: 'auto',
                         flex: 1,
                    }}>
                         {!isEditing && (
                              <Box sx={{
                                   ...styles.fixedSections,
                                   marginBottom: contentGap,
                              }}>
                                   <Box>
                                        {isAddingMilestone ? (
                                             <MilestoneForm
                                                  newMilestoneName={newMilestoneName}
                                                  taskName={newTaskName}
                                                  onMilestoneNameChange={setNewMilestoneName}
                                                  onTaskNameChange={setNewTaskName}
                                                  onSave={handleSaveNewMilestone}
                                                  onCancel={() => { setIsAddingMilestone(false); setIsAddingTaskToExisting(false); setNewMilestoneName(''); setNewTaskName('');}}
                                                  existingTaskNames={localMilestones.map(task => task.taskName.toLowerCase().trim())}
                                                  existingMilestoneNames={localMilestones.map(milestone => milestone.mileStoneName.trim().toLowerCase())}
                                                  currentMilestones={localMilestones}
                                                  isAddingMilestoneToForm={isAddingMilestone}
                                                  isExistingMilestone={isAddingTaskToExisting}
                                                  activateConfirmationDialog={setCanSaveNewMilestone}
                                             />
                                        ) : (
                                             <MilestoneSelector
                                                  selectedMilestone={selectedMilestone}
                                                  availableMilestones={availableMilestones}
                                                  currentMilestones={localMilestones}
                                                  onMilestoneSelect={handleMilestoneSelect}
                                                  onAddMilestone={() => { setIsAddingMilestone(true); setIsAddingTaskToExisting(false); setNewMilestoneName(''); setNewTaskName('');}}
                                                  isAddingMilestone={isAddingMilestone}
                                             />
                                        )}
                                   </Box>
                                   {selectedMilestone && !isAddingMilestone && (
                                        <Box>
                                             <TaskSelector
                                                  selectedTasks={selectedTasks}
                                                  availableTasks={availableTasks}
                                                  selectedMilestone={selectedMilestone}
                                                  onTasksChange={setSelectedTasks}
                                                  currentMilestones={localMilestones}
                                                  onDeleteTask={handleDeleteTask}
                                                  onRenameTask={handleRenameTask}
                                                  onReorderTasks={onReorderTasks}
                                                  allTaskNames={localMilestones.map(task => task.taskName.toLowerCase().trim())}
                                                  allMilestones={localMilestones}
                                             />
                                             <Box sx={styles.taskSelectorHeader}>
                                                  <Typography variant="body2" sx={styles.taskDescription}>
                                                       Add tasks to: <strong>{selectedMilestone}</strong>
                                                  </Typography>
                                                  <Tooltip title={disabledAddTaskButton ? "Maximum number of tasks reached" : "Add task to milestone"} arrow placement="right">
                                                       <span><IconButton
                                                            size="small"
                                                            sx={styles.taskSelectorIconButton}
                                                            onClick={() => {
                                                                 setIsAddingMilestone(true);
                                                                 setNewMilestoneName(selectedMilestone);
                                                                 setIsAddingTaskToExisting(true);
                                                                 setNewTaskName('');
                                                            }}
                                                            disabled={disabledAddTaskButton}
                                                       >
                                                            <AddTaskIcon
                                                                 color={disabledAddTaskButton ? 'disabled' : 'primary'}
                                                                 fontSize="small"
                                                            />
                                                       </IconButton></span>
                                                  </Tooltip>
                                             </Box>
                                        </Box>
                                   )}
                              </Box>
                         )}
                    </Box>
               </Box>
          </Modal>
     );
}; 